/* eslint-disable no-console */
/* eslint-disable @next/next/no-before-interactive-script-outside-document */
/* eslint-disable class-methods-use-this */

import type { FC } from 'react';
import Script from 'next/script';
import { Zones } from '@/types/ads';
import { NcaUrlEvent } from '@/utils/commerce';
import { events$, ADS_UAM } from '@/utils/events';
import type { Size, AdSlotModel } from '@/types/ads';
import type { SiteConfig } from '@/temporary-site-config';

import type { AdService } from './ad-service';
import { AuctionService } from './auction-service';
import { ADHESION_SIZE, isInstreamZone } from './zones';

const APS_AUCTION_TIMEOUT = 1500;
const APS_ACCOUNT_ID = '1506fb3f-088b-4f96-973f-5d623b780f83';

interface AmazonUAMBootstrapProps {
	config: SiteConfig;
}

export const AmazonUAMBootstrap: FC<AmazonUAMBootstrapProps> = ({ config }) => {
	const pubId = config.ad.bidding.vendors?.amazonuam?.config?.pubId;

	return (
		// eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
		<>
			<Script
				id="amazonuamExternal"
				src={`https://config.aps.amazon-adsystem.com/configs/${pubId}`}
				strategy="beforeInteractive"
			/>
			<Script
				id="amazonuamPubExternal"
				src="https://client.aps.amazon-adsystem.com/publisher.js"
				strategy="beforeInteractive"
			/>
			<Script id="amazonuam" strategy="beforeInteractive">
				{`const aps=((e="${pubId}")=>{window._aps=window._aps||new Map,_aps.has(e)||_aps.set(e,{queue:[],store:new Map([["listeners",new Map]])});let t={accountID:e,record:function(e,t){return new Promise((r,n)=>{_aps.get(this.accountID).queue.push(new CustomEvent(e,{detail:{...t,resolve:r,reject:n}}))})},read:function(e){return this.record("bootstrap/store/willRead",{name:e}),_aps.get(this.accountID).store.get(e)},run:function(e,t){_aps.get("_system")?.store.get("listeners").has(e)?this.record(e,t):this.record("bootstrap/run/willDrop",{name:e,detail:t})},react:function(e,t){_aps.get(this.accountID).store.get("listeners").set(e,t);this.record("bootstrap/react/didExecute",{name:e})}};return t.record("bootstrap/version/declare",{version:5}),t})();((t=aps)=>{window.apstag=window.apstag||{init:function(e,r){t.record("legacy/init/enqueue",{config:e,callback:r})},fetchBids:function(e,r){t.record("legacy/fetchBids/enqueue",{bidConfig:e,callback:r})},setDisplayBids:function(){t.run("legacy/setDisplayBids/enqueue",{arguments:arguments})},targetingKeys:function(){return t.read("legacy/targetingKeys")??[]},rpa:function(e,r,n){t.record("legacy/rpa/enqueue",{tokenConfig:e,callback:r,setCookie:n})},upa:function(e,r,n){t.record("legacy/upa/enqueue",{tokenConfig:e,callback:r,setCookie:n})},dpa:function(e){t.record("legacy/dpa/enqueue",{callback:e})}};t.record("bootstrapExtAPStag/version/declare",{version:1})})();((t=aps)=>{window.apstag={...window.apstag,queue:{push:function(e){t.record("legacy/queue/push",{callback:e})}}};t.record("bootstrapExtQueue/version/declare",{version:1})})();((t=aps)=>{window.apstag={...window.apstag,nativeContent:function(){return{enable:function(){t.record("nativeCommerceAds/service/enable")},registerHook:function(e,r){if(e==="nativeCommerceAds.updateClickUrl"){t.react("nativeCommerceAds/clickUrl/update",k=>{return{status:"completed",value:{url:r(k?.detail?.url,k?.detail?.element)}}})}else{throw new Error("Unsupported event type")}}}}};t.record("bootstrapExtNCA/version/declare",{version:1})})();
				window.apstag.init({
					adServer: 'googletag',
					pubID: '${pubId}',
					signals: {
						ortb2: {
							site: {
								page: window.location.href,
								publisher: {
									id: '${APS_ACCOUNT_ID}', // APS Provided - publisher UUID
								},
							},
						},
					},
					videoServer: 'DFP',
				});`}
			</Script>
		</>
	);
};

function buildAdUnit(slotModel: AdSlotModel): ApsSlot | null {
	const { adUnitPath, index, sizes, slotId, zone } = slotModel;
	const displaySizes = sizes?.filter((size: Size) => Array.isArray(size));

	const adUnitPathParts = adUnitPath.split('/') || [];
	adUnitPathParts.splice(2, 1);
	const slotName = `${adUnitPathParts.join('/')}/${zone}-${index}`;
	const isVideo = isInstreamZone(zone);
	const isAdhesionUnit = sizes?.some((size: Size) => size === ADHESION_SIZE);
	const isFixedBottom = zone === Zones.fixed_bottom;
	if (!isVideo && (!displaySizes || displaySizes.length === 0)) {
		return null;
	}

	return {
		mediaType: isVideo ? 'video' : 'display',
		sizes: isVideo ? [[640, 360]] : displaySizes,
		slotID: isVideo ? 'Default' : slotId,
		slotName,
		...(isAdhesionUnit || isFixedBottom
			? {
					slotParams: {
						ggProduct: 'inScreen',
					},
				}
			: {}),
	};
}

export class AmazonUAM extends AuctionService {
	constructor(adService: AdService) {
		super(adService);
		window.apstag?.queue.push(() => {
			window.apstag
				.nativeContent()
				.registerHook('nativeCommerceAds.updateClickUrl', (url, element) => {
					element.dispatchEvent(new NcaUrlEvent(url, element.href));

					return element.href;
				});
			window.apstag.nativeContent().enable();
		});
	}

	runAuction(slotIds: string[]): Promise<ApsAuctionResult | undefined> {
		const slotModels: AdSlotModel[] = this.adService.getSlotModels(slotIds);
		const uamUnits = slotModels.map(buildAdUnit).filter((adUnit) => adUnit);

		events$.next({
			type: `${ADS_UAM}.auction.init`,
			value: { slotModels, uamUnits },
		});
		return new Promise((resolve) => {
			if (!window.apstag || uamUnits.length === 0) {
				resolve(undefined);
				return;
			}

			// const bidMeasure = performanceMeasure('UAM:fetchBids');
			const params: ApsFetchBidsParams = {};
			if (slotModels.some((slotModel) => !!slotModel.refresh)) {
				params.refresh = '1';
			}
			window.apstag.fetchBids(
				{
					params,
					slots: uamUnits,
					// FIXME: should be configurable
					timeout: APS_AUCTION_TIMEOUT,
				},
				(bids: ApsAuctionResult) => {
					// bidMeasure.end();
					events$.next({
						type: `${ADS_UAM}.auction.bidsRecieved`,
						value: { bids },
					});
					window.apstag.setDisplayBids();
					resolve(bids);
				},
			);
		});
	}
}
